/**
 * Freigeben/Ablehnen am Regiebericht (TQ #960). Haengt in Reports.renderReportActions.
 * POST /api/org/:slug/documents/reports/accept|decline?name= mit { confirm: true, reason? };
 * Ablehnen verlangt einen Grund (3 bis 500 Zeichen). Nur Inhaber, nur in der Cockpit-Instanz.
 */
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useToast } from './ui'
import type { RegieberichtBeleg } from '../pages/documents/Reports'
import { canGiveFeedback, feedbackErrorText, reasonValid, REASON_MAX } from '../lib/reportFeedback'

interface Props {
  report: RegieberichtBeleg
  slugOrToken: string
  onDone: () => void
}

const btn: React.CSSProperties = {
  padding: '4px 10px', borderRadius: 4, background: '#fff', cursor: 'pointer',
  fontSize: '0.8125rem', fontWeight: 600,
}

export default function ReportFeedbackActions({ report, slugOrToken, onDone }: Props) {
  const { api } = useAuth()
  const { showToast } = useToast()
  const [mode, setMode] = useState<'accept' | 'decline' | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const sending = useRef(false) // Doppelklick: State ist erst nach dem Render gesetzt
  const dialogRef = useRef<HTMLDivElement>(null)
  const opener = useRef<HTMLElement | null>(null)

  const close = (force = false) => {
    if (!force && (busy || (reason.trim() !== '' && !window.confirm('Ihre Eingabe verwerfen?')))) return
    setMode(null)
    setReason('')
    opener.current?.focus()
  }

  useEffect(() => {
    if (!mode) return
    dialogRef.current?.querySelector<HTMLElement>('textarea, button')?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  // close liest busy/reason; bei jeder Aenderung neu binden
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, busy, reason])

  if (!canGiveFeedback(report)) return null

  const openDialog = (m: 'accept' | 'decline') => (e: React.MouseEvent<HTMLButtonElement>) => {
    opener.current = e.currentTarget
    setReason('')
    setMode(m)
  }

  async function submit(kind: 'accept' | 'decline') {
    if (sending.current || (kind === 'decline' && !reasonValid(reason))) return
    sending.current = true
    setBusy(true)
    try {
      await api!.post(
        `/api/org/${slugOrToken}/documents/reports/${kind}`,
        kind === 'decline' ? { confirm: true, reason: reason.trim() } : { confirm: true },
        { params: { name: report.name } },
      )
      showToast('success', kind === 'accept' ? `Regiebericht ${report.name} freigegeben.` : `Regiebericht ${report.name} abgelehnt.`)
      setBusy(false)
      close(true)
      onDone()
    } catch (e) {
      showToast('error', feedbackErrorText(e, kind))
    } finally {
      sending.current = false
      setBusy(false)
    }
  }

  return (
    <>
      <div style={{ display: 'inline-flex', gap: '0.5rem' }}>
        <button type="button" onClick={openDialog('accept')}
          style={{ ...btn, border: '1px solid #6ee7b7', color: '#047857' }}>✓ Freigeben</button>
        <button type="button" onClick={openDialog('decline')}
          style={{ ...btn, border: '1px solid #fca5a5', color: '#b91c1c' }}>✕ Ablehnen</button>
      </div>
      {mode && (
        <div onClick={() => close()}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: '2rem',
          }}>
          <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={mode === 'accept' ? 'Regiebericht freigeben' : 'Regiebericht ablehnen'}
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', maxWidth: 480, width: '100%', textAlign: 'left' }}>
            <h2 style={{ marginTop: 0 }}>{mode === 'accept' ? 'Regiebericht freigeben?' : 'Regiebericht ablehnen?'}</h2>
            {mode === 'accept' ? (
              <p style={{ color: '#4b5563', lineHeight: 1.5 }}>
                Sie geben den Regiebericht <code>{report.name}</code> frei. Die Rückmeldung ist einmalig und kann nicht geändert werden.
              </p>
            ) : (
              <>
                <p style={{ color: '#4b5563', lineHeight: 1.5 }}>
                  Sie lehnen den Regiebericht <code>{report.name}</code> ab. Die Rückmeldung ist einmalig. Bitte nennen Sie uns den Grund (3 bis 500 Zeichen, ohne die Zeichen &lt; und &gt;).
                </p>
                <label htmlFor="report-decline-reason" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600 }}>
                  Grund der Ablehnung
                </label>
                <textarea id="report-decline-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                  maxLength={REASON_MAX} rows={4}
                  style={{
                    width: '100%', boxSizing: 'border-box', marginTop: 4, padding: '0.5rem',
                    border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem',
                  }} />
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button type="button" onClick={() => close()} disabled={busy}
                style={{ padding: '0.625rem 1rem', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.875rem' }}>
                Abbrechen
              </button>
              <button type="button" onClick={() => submit(mode)}
                disabled={busy || (mode === 'decline' && !reasonValid(reason))}
                style={{
                  padding: '0.625rem 1rem', border: 'none', borderRadius: 6, color: '#fff',
                  fontSize: '0.875rem', fontWeight: 600, cursor: busy ? 'wait' : 'pointer',
                  background: mode === 'accept' ? '#059669' : reasonValid(reason) ? '#dc2626' : '#fca5a5',
                }}>
                {busy ? 'Wird gesendet …' : mode === 'accept' ? 'Freigeben' : 'Ablehnen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
